import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface UseLeadsOptions {
  select?: 'basic' | 'extended';
  limit?: number;
  enabled?: boolean;
  search?: string;
  /** Minimum characters required before fetching (for lazy search) */
  minSearchLength?: number;
}

export function useLeads(options?: UseLeadsOptions) {
  const { select = 'basic', limit, enabled = true, search, minSearchLength = 0 } = options || {};
  const { user } = useAuth();

  // Only enable query if search meets minimum length requirement (for lazy loading)
  const searchEnabled = minSearchLength > 0 ? (search?.length || 0) >= minSearchLength : true;

  return useQuery({
    queryKey: ['leads-list', select, limit, search, user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Use the RPC function which has proper RLS handling
      const { data, error } = await supabase.rpc('get_leads_with_stats', {
        p_user_id: user.id,
        p_filters: search ? { search } : {},
        p_page: 1,
        p_page_size: limit || (minSearchLength > 0 ? 50 : 10000)
      });

      if (error) throw error;
      
      // Extract leads from the RPC response
      const leads = (data as any)?.leads || [];
      
      // Map to expected format
      return leads.map((l: any) => ({
        id: l.id,
        name: l.name,
        email: l.email,
        auskunftsvollmacht: l.auskunftsvollmacht,
        assigned_partner_id: l.assigned_partner_id
      }));
    },
    staleTime: 2 * 60 * 1000, // 2 minutes cache
    enabled: enabled && searchEnabled && !!user?.id,
  });
}
